package ai.jessie.web.customers

import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.settings.FocusReturn
import ai.jessie.web.theme.AlertListStyle
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.ButtonVariant
import ai.jessie.web.theme.MutedStyle
import ai.jessie.web.theme.SmallStyle
import ai.jessie.web.theme.StatusStyle
import ai.jessie.web.theme.formSubmitButton
import ai.jessie.web.theme.slideOver
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul
import org.w3c.dom.HTMLElement

// UC-012 slide-over around the unchanged three-step flow: the form (steps 4–5), then the review
// (step 7) inside the same panel. Closing cancels (A12); nothing is saved before confirmation.
@Composable
internal fun customerEditor(state: CustomerUiState, actions: CustomerActions) {
    val focusReturn = remember { FocusReturn().also { it.pending = true } }
    val reviewing = state.preview != null
    val duplicates = state.preview?.duplicates.orEmpty()
    slideOver(
        panelId = CUSTOMER_EDITOR_ID,
        labelledBy = if (reviewing) CUSTOMER_REVIEW_TITLE_ID else CUSTOMER_TITLE_ID,
        close = actions.cancel,
        busy = state.busy,
        heading = {
            if (reviewing) customerReviewHeading(state)
            else {
                customerHeading(
                    if (state.current == null) "Firma oder Person erfassen"
                    else "Firma oder Person bearbeiten",
                    state,
                    focusReturn,
                )
                customerTypes[state.draft?.type.orEmpty()]?.let {
                    P(attrs = listOf(SmallStyle, MutedStyle).toAttrs()) { Text(it) }
                }
            }
        },
        actions = {
            if (reviewing) {
                themedButton("Zurück", actions.revise, disabled = state.busy)
                themedButton(
                    "Speichern",
                    actions.confirm,
                    variant = ButtonVariant.PRIMARY,
                    disabled = state.busy || (duplicates.isNotEmpty() && !state.duplicateConfirmed),
                )
            } else {
                themedButton("Abbrechen", actions.cancel, disabled = state.busy)
                if (state.error?.authenticationRequired != true)
                    formSubmitButton("Prüfen", CUSTOMER_FORM_ID, disabled = state.busy)
            }
        },
    ) {
        state.error?.let { Div(attrs = AlertStyle.toAttrs()) { onboardingProblem(it, state.busy) } }
        if (state.busy)
            P(attrs = StatusStyle.toAttrs { attr("role", "status") }) {
                Text("Wird geladen oder gespeichert …")
            }
        customerProblems(state.problems)
        if (state.error?.authenticationRequired != true) {
            if (reviewing) customerReview(state, actions) else customerForm(state, actions)
        }
    }
}

@Composable
internal fun customerHeading(title: String, state: CustomerUiState, focusReturn: FocusReturn) {
    H2(
        attrs = {
            id(CUSTOMER_TITLE_ID)
            attr("tabindex", "-1")
        }
    ) {
        Text(title)
    }
    LaunchedEffect(Unit) {
        // While a problem is shown, the alert or the marked field keeps focus.
        if (focusReturn.pending && state.error == null && !AppGlobals.isExporting)
            (document.getElementById(CUSTOMER_TITLE_ID) as? HTMLElement)?.focus()
        // Every later view replaces the control that had focus; only the first render keeps
        // the page title focused.
        focusReturn.pending = true
    }
}

// A6: every invalid field with its correction; marked fields refer to this list.
@Composable
private fun customerProblems(problems: Map<String, String>) {
    if (problems.isEmpty()) return
    Ul(attrs = AlertListStyle.toAttrs { id("customer-problems") }) {
        problems.forEach { (field, correction) ->
            Li { Text("${customerFieldLabel(field)}: $correction") }
        }
    }
}

internal const val CUSTOMER_EDITOR_ID = "customer-editor"
internal const val CUSTOMER_FORM_ID = "customer-form"
